package com.flowyride.feature.account.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.PrimaryTabRow
import androidx.compose.material3.Surface
import androidx.compose.material3.Tab
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.flowyride.core.designsystem.component.FlowyTextField
import com.flowyride.core.designsystem.component.FlowyTextFieldKind
import com.flowyride.feature.account.AuthAction
import com.flowyride.feature.account.AuthMode
import com.flowyride.feature.account.AuthUiState
import com.flowyride.feature.account.AuthViewModel
import com.flowyride.feature.account.resources.Res
import com.flowyride.feature.account.resources.action_request
import com.flowyride.feature.account.resources.action_sign_in
import com.flowyride.feature.account.resources.app_name
import com.flowyride.feature.account.resources.auth_subtitle
import com.flowyride.feature.account.resources.field_email
import com.flowyride.feature.account.resources.field_login
import com.flowyride.feature.account.resources.field_message
import com.flowyride.feature.account.resources.field_new_password
import com.flowyride.feature.account.resources.field_password
import com.flowyride.feature.account.resources.field_username
import com.flowyride.feature.account.resources.field_username_hint
import com.flowyride.feature.account.resources.hide_password
import com.flowyride.feature.account.resources.request_sent
import com.flowyride.feature.account.resources.show_password
import com.flowyride.feature.account.resources.tab_request
import com.flowyride.feature.account.resources.tab_sign_in
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun AuthRoute(viewModel: AuthViewModel = koinViewModel()) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    AuthScreen(state, viewModel::onAction)
}

@Composable
fun AuthScreen(state: AuthUiState, onAction: (AuthAction) -> Unit) {
    Surface(Modifier.fillMaxSize(), color = MaterialTheme.colorScheme.surfaceContainerLow) {
        Box(
            Modifier.fillMaxSize().safeDrawingPadding().imePadding().verticalScroll(rememberScrollState()),
            contentAlignment = Alignment.TopCenter,
        ) {
            Surface(
                Modifier.padding(16.dp).widthIn(max = 420.dp).fillMaxWidth(),
                shape = MaterialTheme.shapes.large,
                tonalElevation = 2.dp,
            ) {
                Column(Modifier.padding(20.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text(stringResource(Res.string.app_name), style = MaterialTheme.typography.headlineMedium, color = MaterialTheme.colorScheme.primary)
                    Text(stringResource(Res.string.auth_subtitle), style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
                    PrimaryTabRow(selectedTabIndex = state.mode.ordinal) {
                        Tab(
                            selected = state.mode == AuthMode.SignIn,
                            onClick = { onAction(AuthAction.ModeSelected(AuthMode.SignIn)) },
                            text = { Text(stringResource(Res.string.tab_sign_in)) },
                        )
                        Tab(
                            selected = state.mode == AuthMode.Request,
                            onClick = { onAction(AuthAction.ModeSelected(AuthMode.Request)) },
                            text = { Text(stringResource(Res.string.tab_request)) },
                        )
                    }
                    when (state.mode) {
                        AuthMode.SignIn -> SignInForm(state, onAction)
                        AuthMode.Request -> RequestForm(state, onAction)
                    }
                    state.error?.let {
                        Text(stringResource(it), color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodyMedium)
                    }
                }
            }
        }
    }
}

@Composable
private fun SignInForm(state: AuthUiState, onAction: (AuthAction) -> Unit) {
    if (state.requestSent) {
        Text(stringResource(Res.string.request_sent), style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.primary)
    }
    FlowyTextField(
        value = state.login,
        onValueChange = { onAction(AuthAction.LoginChanged(it)) },
        label = stringResource(Res.string.field_login),
        kind = FlowyTextFieldKind.Username,
        imeAction = ImeAction.Next,
        modifier = Modifier.fillMaxWidth(),
    )
    PasswordField(
        value = state.password,
        onValueChange = { onAction(AuthAction.PasswordChanged(it)) },
        label = stringResource(Res.string.field_password),
        kind = FlowyTextFieldKind.CurrentPassword,
        imeAction = ImeAction.Done,
        onDone = { onAction(AuthAction.SignIn) },
    )
    SubmitButton(stringResource(Res.string.action_sign_in), state.busy) { onAction(AuthAction.SignIn) }
}

@Composable
private fun RequestForm(state: AuthUiState, onAction: (AuthAction) -> Unit) {
    FlowyTextField(
        value = state.username,
        onValueChange = { onAction(AuthAction.UsernameChanged(it)) },
        label = stringResource(Res.string.field_username),
        supportingText = stringResource(Res.string.field_username_hint),
        kind = FlowyTextFieldKind.Username,
        imeAction = ImeAction.Next,
        modifier = Modifier.fillMaxWidth(),
    )
    FlowyTextField(
        value = state.email,
        onValueChange = { onAction(AuthAction.EmailChanged(it)) },
        label = stringResource(Res.string.field_email),
        kind = FlowyTextFieldKind.Email,
        imeAction = ImeAction.Next,
        modifier = Modifier.fillMaxWidth(),
    )
    PasswordField(
        value = state.newPassword,
        onValueChange = { onAction(AuthAction.NewPasswordChanged(it)) },
        label = stringResource(Res.string.field_new_password),
        kind = FlowyTextFieldKind.NewPassword,
        imeAction = ImeAction.Next,
    )
    FlowyTextField(
        value = state.message,
        onValueChange = { onAction(AuthAction.MessageChanged(it)) },
        label = stringResource(Res.string.field_message),
        kind = FlowyTextFieldKind.Message,
        modifier = Modifier.fillMaxWidth(),
    )
    SubmitButton(stringResource(Res.string.action_request), state.busy) { onAction(AuthAction.SendRequest) }
}

@Composable
private fun PasswordField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    kind: FlowyTextFieldKind,
    imeAction: ImeAction,
    onDone: () -> Unit = {},
) {
    var visible by rememberSaveable { mutableStateOf(false) }
    FlowyTextField(
        value = value,
        onValueChange = onValueChange,
        label = label,
        kind = kind,
        passwordVisible = visible,
        imeAction = imeAction,
        onImeAction = onDone,
        trailingIcon = {
            TextButton(onClick = { visible = !visible }) {
                Text(stringResource(if (visible) Res.string.hide_password else Res.string.show_password))
            }
        },
        modifier = Modifier.fillMaxWidth(),
    )
}

@Composable
private fun SubmitButton(text: String, busy: Boolean, onClick: () -> Unit) {
    Button(onClick = onClick, enabled = !busy, modifier = Modifier.fillMaxWidth()) {
        if (busy) CircularProgressIndicator(Modifier.size(18.dp), strokeWidth = 2.dp) else Text(text)
    }
}
